import { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Heading,
  IconButton,
  Collapse,
  useColorModeValue,
  Icon,
  HStack,
  Alert,
  AlertIcon,
  AlertDescription,
  Text,
} from '@chakra-ui/react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  WarningIcon,
} from '@chakra-ui/icons';
import { motion } from 'framer-motion';
import Card from 'components/card/Card';

interface CollapsibleSectionProps {
  id: string;
  title: string;
  children: React.ReactNode;
  defaultIsOpen?: boolean;
  isActive?: boolean;
  highlightTrigger?: number;
  isValid?: boolean;
  message?: string | null;
}

const CollapsibleSection = ({
  id,
  title,
  children,
  defaultIsOpen = true,
  isActive = false,
  highlightTrigger = 0,
  isValid,
  message,
}: CollapsibleSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultIsOpen);
  const [showHighlight, setShowHighlight] = useState(false);
  const bgColor = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const textColor = useColorModeValue('gray.700', 'white');
  const activeBorderColor = useColorModeValue('gray.500', 'gray.400');
  const warningBorderColor = useColorModeValue('orange.400', 'orange.300');

  // Trigger highlight animation when highlightTrigger changes
  useEffect(() => {
    if (highlightTrigger > 0) {
      setShowHighlight(true);
      const timer = setTimeout(() => {
        setShowHighlight(false);
      }, 2000); // Show highlight for 2 seconds
      return () => clearTimeout(timer);
    }
  }, [highlightTrigger]);

  const MotionCard = motion(Card);

  const getStatusIcon = () => {
    if (isValid === undefined) return null;

    return !isValid ? (
      <Icon as={WarningIcon} color="orange.500" boxSize={5} />
    ) : (
      <Icon as={CheckCircleIcon} color="green.500" boxSize={5} />
    );
  };

  return (
    <MotionCard
      id={id}
      bg={bgColor}
      p={0}
      mb={4}
      borderWidth="1px"
      borderColor={
        isActive
          ? isValid === false
            ? warningBorderColor
            : activeBorderColor
          : borderColor
      }
      animate={{
        boxShadow: showHighlight
          ? [
              `0 0 0 0px ${
                isActive && isValid === false
                  ? 'rgba(251, 146, 60, 0.4)'
                  : 'rgba(107, 114, 128, 0.4)'
              }`,
              `0 0 0 10px ${
                isActive && isValid === false
                  ? 'rgba(251, 146, 60, 0)'
                  : 'rgba(107, 114, 128, 0)'
              }`,
              `0 0 0 0px ${
                isActive && isValid === false
                  ? 'rgba(251, 146, 60, 0)'
                  : 'rgba(107, 114, 128, 0)'
              }`,
            ]
          : '0 0 0 0px rgba(107, 114, 128, 0)',
      }}
      transition={{
        duration: 1.5,
        ease: 'easeOut',
      }}
    >
      <Flex
        justify="space-between"
        align="center"
        p={4}
        borderBottom={isOpen ? '1px solid' : 'none'}
        borderColor={borderColor}
        cursor="pointer"
        onClick={() => setIsOpen(!isOpen)}
        _hover={{
          bg: useColorModeValue('gray.50', 'navy.700'),
        }}
        borderTopRadius="inherit"
        borderRadius={isOpen ? 'auto' : 'inherit'}
        transition="background 0.2s"
      >
        <Heading size="sm" color={textColor}>
          {title}
        </Heading>
        <HStack spacing={2}>
          {getStatusIcon()}
          <IconButton
            aria-label={isOpen ? 'Collapse section' : 'Expand section'}
            icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
          />
        </HStack>
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        <Box p={4}>
          {message && (
            <Alert status={isValid ? 'success' : 'warning'} mb={4}>
              <AlertIcon />
              <AlertDescription>
                <Text fontWeight="semibold">
                  {isValid ? 'No Issues Found' : 'Caution'}
                </Text>
                <Text mt={1} fontSize="sm">
                  {message}
                </Text>
              </AlertDescription>
            </Alert>
          )}
          {children}
        </Box>
      </Collapse>
    </MotionCard>
  );
};

export default CollapsibleSection;
